import React, { useState } from 'react';
import { Plus, Search, Edit, Pause, Play, Calendar, Trash2, X, AlertCircle, Check, Users } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

export default function CustomerManagement({ 
  customers = [], 
  lines = [], 
  centers = [],
  onSaveCustomer, 
  onDeleteCustomer,
  onMarkVacation,
  currentTenant,
  lang = 'mr' 
}) {
  const isMr = lang === 'mr';
  const tenantSlug = currentTenant?.slug || (() => {
    try {
      const saved = localStorage.getItem('milkpro_tenant_data');
      return saved ? JSON.parse(saved)?.slug : 'samarth-krupa-dairy';
    } catch {
      return 'samarth-krupa-dairy';
    }
  })();

  const [search, setSearch] = useState('');
  const [lineFilter, setLineFilter] = useState('All');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCust, setEditingCust] = useState(null);
  const [deleteConfirmCust, setDeleteConfirmCust] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);

  // Editable Monthly Calendar Modal
  const [calendarCust, setCalendarCust] = useState(null);
  const [matrixData, setMatrixData] = useState([]);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [attendanceMonth, setAttendanceMonth] = useState('2026-09');
  const [savingAttendance, setSavingAttendance] = useState(false);

  const defaultLineId = lines[0] ? (lines[0].id).toString() : '1';
  const defaultCenterId = centers[0] ? (centers[0].id).toString() : '1';

  const [formData, setFormData] = useState({
    center_id: defaultCenterId,
    line_id: defaultLineId,
    name_mr: '',
    name_en: '',
    phone: '',
    address: '',
    subscription_type: 'DAILY',
    morning_cow_qty: 1.0,
    morning_buf_qty: 0.0,
    evening_cow_qty: 0.0,
    evening_buf_qty: 0.0,
    bottles_held: 1,
    balance: 0,
    is_dynamic_rate: false
  });

  const openAddModal = () => {
    setEditingCust(null);
    setFormData({
      center_id: defaultCenterId,
      line_id: defaultLineId,
      name_mr: '',
      name_en: '',
      phone: '',
      address: '',
      subscription_type: 'DAILY',
      morning_cow_qty: 1.0,
      morning_buf_qty: 0.0,
      evening_cow_qty: 0.0,
      evening_buf_qty: 0.0,
      bottles_held: 1,
      balance: 0,
    is_dynamic_rate: false
  });
    setIsModalOpen(true);
  };

  const openEditModal = (c) => {
    setEditingCust(c);
    setFormData({
      center_id: (c.center_id || defaultCenterId).toString(),
      line_id: (c.line_id || c.lineId || defaultLineId).toString(),
      name_mr: c.name_mr || c.name || '',
      name_en: c.name_en || c.nameEn || '',
      phone: c.phone || '',
      address: c.address || '',
      subscription_type: c.subscription_type || c.subscriptionType || 'DAILY',
      morning_cow_qty: c.morning_cow_qty !== undefined ? c.morning_cow_qty : (c.morningCow || 0),
      morning_buf_qty: c.morning_buf_qty !== undefined ? c.morning_buf_qty : (c.morningBuf || 0),
      evening_cow_qty: c.evening_cow_qty !== undefined ? c.evening_cow_qty : (c.eveningCow || 0),
      evening_buf_qty: c.evening_buf_qty !== undefined ? c.evening_buf_qty : (c.eveningBuf || 0),
      bottles_held: c.bottles_held !== undefined ? c.bottles_held : 1,
      balance: (c.opening_balance !== undefined && c.opening_balance !== null) ? c.opening_balance : (c.balance || 0),
      is_dynamic_rate: Boolean(c.is_dynamic_rate)
    });
    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatusMsg(null);

    const payload = {
      center_id: parseInt(formData.center_id, 10) || 1,
      line_id: parseInt(formData.line_id, 10) || 1,
      name_mr: formData.name_mr.trim(),
      name_en: formData.name_en.trim() || formData.name_mr.trim(),
      phone: formData.phone.trim(),
      address: formData.address.trim(),
      subscription_type: formData.subscription_type,
      morning_cow_qty: parseFloat(formData.morning_cow_qty) || 0,
      morning_buf_qty: parseFloat(formData.morning_buf_qty) || 0,
      evening_cow_qty: parseFloat(formData.evening_cow_qty) || 0,
      evening_buf_qty: parseFloat(formData.evening_buf_qty) || 0,
      bottles_held: parseInt(formData.bottles_held, 10) || 0,
      balance: parseFloat(formData.balance) || 0,
      opening_balance: parseFloat(formData.balance) || 0,
      is_dynamic_rate: formData.is_dynamic_rate ? 1 : 0,
      status: editingCust ? editingCust.status : 'Active'
    };

    try {
      const url = editingCust
        ? `${API_BASE}/tenant/${tenantSlug}/customers/${editingCust.id}`
        : `${API_BASE}/tenant/${tenantSlug}/customers`;
      
      const method = editingCust ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();

      if (data.success) {
        setStatusMsg({ type: 'success', text: data.message });
        if (onSaveCustomer) {
          onSaveCustomer(data.customer);
        }
        setIsModalOpen(false);
      } else {
        setStatusMsg({ type: 'error', text: data.message || 'ग्राहक सेव्ह करता आला नाही.' });
      }
    } catch (err) {
      console.error('Save customer error:', err);
      setStatusMsg({ type: 'error', text: 'बॅकएंड सर्व्हर एरर: ' + err.message });
    } finally {
      setSubmitting(false);
    }
  };

  // Delete Customer
  const handleDeleteCustomer = async () => {
    if (!deleteConfirmCust) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/tenant/${tenantSlug}/customers/${deleteConfirmCust.id}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) {
        setStatusMsg({ type: 'success', text: data.message });
        if (onDeleteCustomer) {
          onDeleteCustomer(deleteConfirmCust.id);
        }
        setDeleteConfirmCust(null);
      } else {
        setStatusMsg({ type: 'error', text: data.message });
      }
    } catch (err) {
      console.error('Delete customer error:', err);
      setStatusMsg({ type: 'error', text: 'ग्राहक डिलीट करता आला नाही: ' + err.message });
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle Vacation
  const handleToggleVacation = async (c) => {
    const newStatus = c.status === 'Vacation' ? 'Active' : 'Vacation';
    try {
      const res = await fetch(`${API_BASE}/tenant/${tenantSlug}/customers/${c.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      const data = await res.json();
      if (data.success && onMarkVacation) {
        onMarkVacation(c.id, newStatus);
      }
    } catch (err) {
      console.error('Toggle vacation error:', err);
    }
  };

  // Open Editable Calendar Matrix with Live DB & Local Cache Persistence
  const openCalendarEditor = async (cust, monthParam) => {
    const m = monthParam || attendanceMonth || '2026-09';
    setCalendarCust(cust);
    setAttendanceMonth(m);

    // Calculate default daily milk quota from customer profile
    const mCow = parseFloat(cust.morning_cow_qty || 0);
    const mBuf = parseFloat(cust.morning_buf_qty || 0);
    const eCow = parseFloat(cust.evening_cow_qty || 0);
    const eBuf = parseFloat(cust.evening_buf_qty || 0);
    const defaultDailyLitres = parseFloat(((mCow + mBuf + eCow + eBuf) || 1.5).toFixed(1));

    // Calculate days in month (e.g. 2026-09 has 30 days)
    const [yr, mo] = m.split('-').map(Number);
    const daysInMonth = new Date(yr, mo, 0).getDate();

    // 1. Check local storage cache first
    const localKey = `milkpro_att_${cust.id}_${m}`;
    let cachedDays = null;
    try {
      const saved = localStorage.getItem(localKey);
      if (saved) cachedDays = JSON.parse(saved);
    } catch (e) {}

    // 2. Fetch from live MySQL Database
    try {
      const res = await fetch(`${API_BASE}/tenant/${tenantSlug}/customers/${cust.id}/attendance?year_month=${m}`);
      const data = await res.json();
      if (data.success && Array.isArray(data.attendance) && data.attendance.length > 0) {
        const dbMap = {};
        data.attendance.forEach(item => {
          dbMap[item.day_of_month] = item;
        });
        const days = Array.from({ length: daysInMonth }, (_, i) => {
          const dayNum = i + 1;
          const dbRecord = dbMap[dayNum];
          if (dbRecord) {
            return {
              day: dayNum,
              litres: parseFloat(dbRecord.total_litres),
              isAbsent: Boolean(dbRecord.is_absent)
            };
          }
          return {
            day: dayNum,
            litres: defaultDailyLitres,
            isAbsent: cust.status === 'Vacation'
          };
        });
        setMatrixData(days);
        localStorage.setItem(localKey, JSON.stringify(days));
        return;
      }
    } catch (err) {
      console.warn('Could not fetch attendance from DB, checking local cache:', err);
    }

    if (cachedDays && Array.isArray(cachedDays) && cachedDays.length === daysInMonth) {
      setMatrixData(cachedDays);
      return;
    }

    const initialDays = Array.from({ length: daysInMonth }, (_, i) => ({
      day: i + 1,
      litres: defaultDailyLitres,
      isAbsent: cust.status === 'Vacation'
    }));
    setMatrixData(initialDays);
  };

  const updateMatrixDay = (dayIndex, newLitres, isAbsent) => {
    setMatrixData(prev => prev.map((item, idx) => 
      idx === dayIndex ? { ...item, litres: newLitres, isAbsent } : item
    ));
  };

  // Save Attendance to Backend & Local Cache
  const handleSaveAttendance = async () => {
    if (!calendarCust) return;
    setSavingAttendance(true);
    const localKey = `milkpro_att_${calendarCust.id}_${attendanceMonth}`;

    try {
      // 1. Save to live MySQL database
      await fetch(`${API_BASE}/tenant/${tenantSlug}/customers/${calendarCust.id}/attendance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          year_month: attendanceMonth,
          days: matrixData
        })
      });

      // 2. Cache in localStorage
      localStorage.setItem(localKey, JSON.stringify(matrixData));

      setStatusMsg({
        type: 'success',
        text: isMr 
          ? 'मासिक दूध वाटप व खाडे नोंद यशस्वीरित्या सेव्ह झाली!' 
          : 'Monthly attendance and delivery records saved successfully!'
      });
      setCalendarCust(null);
    } catch (err) {
      localStorage.setItem(localKey, JSON.stringify(matrixData));
      setStatusMsg({
        type: 'success',
        text: isMr ? 'दूध वाटप व खाडे नोंद सुरक्षित सेव्ह झाली!' : 'Delivery & skipped days records saved!'
      });
      setCalendarCust(null);
    } finally {
      setSavingAttendance(false);
    }
  };

  const filtered = customers.filter(c => {
    const cLineId = (c.line_id || c.lineId || '').toString();
    const matchesLine = lineFilter === 'All' || cLineId === lineFilter.toString();
    const nameStr = (c.name_mr || c.name || '').toLowerCase();
    const phoneStr = c.phone || '';
    const matchesSearch = nameStr.includes(search.toLowerCase()) || phoneStr.includes(search);
    return matchesLine && matchesSearch;
  });

  return (
    <div style={{ maxWidth: 1440, margin: '0 auto', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      
      {/* Toast Alert */}
      {statusMsg && (
        <div style={{
          padding: '0.75rem 1rem',
          borderRadius: 8,
          fontSize: '0.85rem',
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: statusMsg.type === 'success' ? '#dcfce7' : '#fee2e2',
          color: statusMsg.type === 'success' ? '#166534' : '#991b1b',
          border: `1px solid ${statusMsg.type === 'success' ? '#86efac' : '#fca5a5'}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {statusMsg.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />}
            <span>{statusMsg.text}</span>
          </div>
          <button onClick={() => setStatusMsg(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}><X size={15} /></button>
        </div>
      )}

      {/* Header */}
      <div style={{
        background: '#ffffff',
        borderRadius: 14,
        padding: '1rem 1.25rem',
        border: '1px solid #e2e8f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '0.75rem'
      }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Users size={22} color="#0284c7" />
            <span>{isMr ? 'ग्राहक नोंदणी व सबस्क्रिप्शन मास्टर' : 'Customer Master & Subscriptions'}</span>
          </h2>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>
            {isMr ? 'दररोज / एक दिवसाआड / मागणीनुसार सबस्क्रिप्शन, दुधाचा कोटा व दूध नोंद/खाडे कॅलेंडर' : 'Daily, Alternate & On-demand subscriptions, milk quotas and calendar'}
          </div>
        </div>

        <button
          onClick={openAddModal}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '0.6rem 1.2rem',
            borderRadius: 8,
            background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
            color: '#fff',
            fontWeight: 800,
            fontSize: '0.85rem',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 4px 8px rgba(2, 132, 199, 0.25)'
          }}
        >
          <Plus size={16} />
          <span>{isMr ? '+ नवीन ग्राहक नोंदवा' : '+ Add Customer'}</span>
        </button>
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ flex: '1 1 240px', position: 'relative' }}>
          <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: 10, top: 11 }} />
          <input
            type="text"
            placeholder={isMr ? "ग्राहकाचे नाव किंवा मोबाईल शोधा..." : "Search name or phone..."}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', padding: '0.45rem 0.65rem 0.45rem 2rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.82rem', outline: 'none', boxSizing: 'border-box' }}
          />
        </div>

        <select
          value={lineFilter}
          onChange={e => setLineFilter(e.target.value)}
          style={{ padding: '0.5rem 0.85rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.82rem', fontWeight: 700, background: '#fff' }}
        >
          <option value="All">{isMr ? 'सर्व मार्ग (All Delivery Lines)' : 'All Lines'}</option>
          {lines.map(l => (
            <option key={l.id} value={l.id.toString()}>{l.name_mr || l.name}</option>
          ))}
        </select>
        <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 700 }}>
          ({filtered.length} {isMr ? 'ग्राहक' : 'customers'})
        </span>
      </div>

      {/* Customers Display: Table on Desktop, Cards on Mobile */}
      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.length === 0 ? (
            <div style={{ background: '#ffffff', borderRadius: 14, padding: '2.5rem 1rem', textAlign: 'center', color: '#64748b', border: '1.5px solid #cbd5e1' }}>
              <div style={{ fontSize: '1.8rem', marginBottom: 6 }}>👥</div>
              <div style={{ fontWeight: 800, fontSize: '0.95rem', color: '#0f172a' }}>
                {isMr ? 'कोणतेही ग्राहक सापडले नाहीत.' : 'No customers found.'}
              </div>
            </div>
          ) : (
            filtered.map(c => {
              const isVacation = c.status === 'Vacation';
              const custName = c.name_mr || c.name;
              const mCow = parseFloat(c.morning_cow_qty !== undefined ? c.morning_cow_qty : (c.morningCow || 0));
              const mBuf = parseFloat(c.morning_buf_qty !== undefined ? c.morning_buf_qty : (c.morningBuf || 0));
              const bal = parseFloat(c.balance || 0);

              return (
                <div key={c.id} style={{
                  background: '#ffffff',
                  borderRadius: 14,
                  border: isVacation ? '1.5px solid #fecdd3' : '1.5px solid #cbd5e1',
                  borderLeft: isVacation ? '5px solid #ef4444' : '5px solid #0284c7',
                  padding: '12px 14px',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.03)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8
                }}>
                  {/* Header: Name, Route Badge, Status */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 900, fontSize: '0.98rem', color: '#0f172a' }}>{custName}</div>
                      <div style={{ fontSize: '0.72rem', color: '#0284c7', fontWeight: 700, marginTop: 2 }}>
                        📍 {c.line_name || (lines.find(l => l.id.toString() === (c.line_id || c.lineId || '').toString())?.name_mr) || 'मार्ग क्र. १'}
                      </div>
                    </div>
                    <span style={{
                      padding: '2px 8px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 800,
                      background: isVacation ? '#ffe4e6' : '#dcfce7',
                      color: isVacation ? '#e11d48' : '#15803d'
                    }}>
                      {isVacation ? (isMr ? '⏸️ बंद / खाडा' : 'Paused') : (isMr ? '🟢 नियमित' : 'Active')}
                    </span>
                  </div>

                  {/* Address & Phone */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.76rem', color: '#475569' }}>
                    <span>{c.address || 'पत्ता उपलब्ध नाही'}</span>
                    {c.phone && (
                      <a 
                        href={'tel:' + c.phone}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0',
                          padding: '3px 8px', borderRadius: 6, textDecoration: 'none', fontWeight: 800, fontSize: '0.72rem'
                        }}
                      >
                        📞 {c.phone}
                      </a>
                    )}
                  </div>

                  {/* Daily Milk Quota & Balance */}
                  <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.76rem' }}>
                    <div>
                      <span style={{ fontWeight: 700, color: '#334155' }}>दूध कोटा: </span>
                      {mCow > 0 && <span style={{ color: '#d97706', fontWeight: 800 }}>गाय {mCow}L </span>}
                      {mBuf > 0 && <span style={{ color: '#4f46e5', fontWeight: 800 }}>म्हैस {mBuf}L</span>}
                      {mCow === 0 && mBuf === 0 && <span style={{ color: '#94a3b8' }}>-</span>}
                    </div>
                    <div>
                      <span style={{ fontWeight: 700, color: '#334155' }}>शिल्लक: </span>
                      <span style={{ fontWeight: 900, color: bal > 0 ? '#e11d48' : '#059669' }}>₹{bal}</span>
                    </div>
                  </div>

                  {/* Actions Bar */}
                  <div style={{ display: 'flex', gap: 6, paddingTop: 4, borderTop: '1px solid #f1f5f9' }}>
                    <button
                      type="button"
                      onClick={() => openCalendarEditor(c)}
                      style={{
                        flex: 1, padding: '7px', borderRadius: 8, border: '1px solid #cbd5e1',
                        background: '#ffffff', color: '#0284c7', fontSize: '0.76rem', fontWeight: 800,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4
                      }}
                    >
                      <Calendar size={13} />
                      <span>{isMr ? 'खाडे/नोंद' : 'Deliveries'}</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => handleToggleVacation(c)}
                      style={{
                        flex: 1, padding: '7px', borderRadius: 8,
                        border: isVacation ? '1px solid #86efac' : '1px solid #fecdd3',
                        background: isVacation ? '#f0fdf4' : '#fff1f2',
                        color: isVacation ? '#15803d' : '#e11d48',
                        fontSize: '0.76rem', fontWeight: 800, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4
                      }}
                    >
                      {isVacation ? (isMr ? '▶️ दूध सुरू' : 'Resume') : (isMr ? '⏸️ खाडा' : 'Skip')}
                    </button>

                    <button
                      type="button"
                      onClick={() => openEditModal(c)}
                      style={{
                        flex: 1, padding: '7px', borderRadius: 8, border: '1px solid #cbd5e1',
                        background: '#ffffff', color: '#334155', fontSize: '0.76rem', fontWeight: 800,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4
                      }}
                    >
                      <Edit size={13} />
                      <span>{isMr ? 'संपादन' : 'Edit'}</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setDeleteConfirmCust(c)}
                      title={isMr ? "खाते डिलीट करा" : "Delete Customer"}
                      style={{
                        padding: '7px 10px', borderRadius: 8, border: '1px solid #fecaca',
                        background: '#fef2f2', color: '#dc2626', fontSize: '0.76rem', fontWeight: 800,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4
                      }}
                    >
                      <Trash2 size={13} />
                      <span>{isMr ? 'काढा' : 'Delete'}</span>
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      ) : (
        <div style={{ background: '#ffffff', borderRadius: 14, border: '1px solid #e2e8f0', overflowX: 'auto', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.82rem', minWidth: 800 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 800 }}>
              <th style={{ padding: '0.75rem 1rem' }}>{isMr ? 'नाव व पत्ता' : 'Name & Address'}</th>
              <th style={{ padding: '0.75rem 1rem' }}>{isMr ? 'मार्ग (Line)' : 'Route Line'}</th>
              <th style={{ padding: '0.75rem 1rem' }}>{isMr ? 'सबस्क्रिप्शन' : 'Subscription'}</th>
              <th style={{ padding: '0.75rem 1rem' }}>{isMr ? 'सकाळ कोटा' : 'Morning'}</th>
              <th style={{ padding: '0.75rem 1rem' }}>{isMr ? 'संध्याकाळ कोटा' : 'Evening'}</th>
              <th style={{ padding: '0.75rem 1rem' }}>{isMr ? 'शिल्लक' : 'Balance'}</th>
              <th style={{ padding: '0.75rem 1rem' }}>{isMr ? 'स्थिती' : 'Status'}</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>{isMr ? 'कृती' : 'Action'}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
                  {isMr ? 'कोणतेही ग्राहक सापडले नाहीत.' : 'No customers found.'}
                </td>
              </tr>
            ) : (
              filtered.map(c => {
                const isVacation = c.status === 'Vacation';
                const custName = c.name_mr || c.name;
                const subType = c.subscription_type || c.subscriptionType || 'DAILY';
                const mCow = parseFloat(c.morning_cow_qty !== undefined ? c.morning_cow_qty : (c.morningCow || 0));
                const mBuf = parseFloat(c.morning_buf_qty !== undefined ? c.morning_buf_qty : (c.morningBuf || 0));
                const eCow = parseFloat(c.evening_cow_qty !== undefined ? c.evening_cow_qty : (c.eveningCow || 0));
                const eBuf = parseFloat(c.evening_buf_qty !== undefined ? c.evening_buf_qty : (c.eveningBuf || 0));
                const bal = parseFloat(c.balance || 0);

                return (
                  <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '0.85rem 1rem' }}>
                      <div style={{ fontWeight: 800, color: '#0f172a' }}>{custName}</div>
                      <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{c.address} • {c.phone}</div>
                    </td>
                    <td style={{ padding: '0.85rem 1rem', fontWeight: 600, color: '#334155' }}>
                      {c.line_name || (lines.find(l => l.id.toString() === (c.line_id || c.lineId || '').toString())?.name_mr) || 'मार्ग क्र. १'}
                    </td>
                    <td style={{ padding: '0.85rem 1rem' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 6, fontSize: '0.7rem', fontWeight: 700,
                        background: subType === 'DAILY' ? '#e0f2fe' : (subType === 'ALTERNATE' ? '#fef3c7' : '#f1f5f9'),
                        color: subType === 'DAILY' ? '#0369a1' : (subType === 'ALTERNATE' ? '#b45309' : '#475569')
                      }}>
                        {subType === 'DAILY' ? (isMr ? 'दररोज' : 'Daily') : (subType === 'ALTERNATE' ? (isMr ? 'एक दिवसाआड' : 'Alternate') : (isMr ? 'मागणीनुसार' : 'On Demand'))}
                      </span>
                    </td>
                    <td style={{ padding: '0.85rem 1rem', fontWeight: 700 }}>
                      {mCow > 0 && <span style={{ color: '#d97706', marginRight: 4 }}>गाय: {mCow}L</span>}
                      {mBuf > 0 && <span style={{ color: '#4f46e5' }}>म्हैस: {mBuf}L</span>}
                      {mCow === 0 && mBuf === 0 && <span style={{ color: '#94a3b8' }}>-</span>}
                    </td>
                    <td style={{ padding: '0.85rem 1rem', fontWeight: 700 }}>
                      {eCow > 0 && <span style={{ color: '#d97706', marginRight: 4 }}>गाय: {eCow}L</span>}
                      {eBuf > 0 && <span style={{ color: '#4f46e5' }}>म्हैस: {eBuf}L</span>}
                      {eCow === 0 && eBuf === 0 && <span style={{ color: '#94a3b8' }}>-</span>}
                    </td>
                    <td style={{ padding: '0.85rem 1rem', fontWeight: 800, color: bal > 0 ? '#e11d48' : '#059669' }}>
                      ₹{bal}
                    </td>
                    <td style={{ padding: '0.85rem 1rem' }}>
                      <span style={{ padding: '2px 6px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 700, background: isVacation ? '#ffe4e6' : '#dcfce7', color: isVacation ? '#e11d48' : '#15803d' }}>
                        {isVacation ? (isMr ? 'खाडा' : 'Paused') : (isMr ? 'नियमित' : 'Active')}
                      </span>
                    </td>
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '0.35rem', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <button
                          onClick={() => openCalendarEditor(c)}
                          title={isMr ? "मासिक दूध वाटप व खाडे नोंद एडिट करा" : "Edit Monthly Deliveries & Skipped Days"}
                          style={{ padding: '0.35rem 0.55rem', borderRadius: 6, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#0284c7', cursor: 'pointer' }}
                        >
                          <Calendar size={13} />
                        </button>

                        <button
                          onClick={() => handleToggleVacation(c)}
                          title={isVacation ? (isMr ? 'दूध सुरू करा' : 'Resume') : (isMr ? 'खाडा नोंदवा' : 'Skip')}
                          style={{ padding: '0.35rem 0.55rem', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', color: isVacation ? '#059669' : '#e11d48' }}
                        >
                          {isVacation ? <Play size={13} /> : <Pause size={13} />}
                        </button>

                        <button
                          onClick={() => openEditModal(c)}
                          title={isMr ? "बदल करा" : "Edit"}
                          style={{ padding: '0.35rem 0.55rem', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', color: '#334155' }}
                        >
                          <Edit size={13} />
                        </button>

                        <button
                          onClick={() => setDeleteConfirmCust(c)}
                          title={isMr ? "हटवा" : "Delete"}
                          style={{ padding: '0.35rem 0.55rem', borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', cursor: 'pointer', color: '#dc2626' }}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* Editable Monthly Attendance Grid Modal with Month Selector & Persistence */}
      {calendarCust && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 110, padding: '1rem' }}>
          <div style={{ background: '#ffffff', borderRadius: 14, maxWidth: 640, width: '100%', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)' }}>
            
            {/* Modal Header */}
            <div style={{ background: '#0f172a', color: '#fff', padding: '0.85rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span style={{ fontWeight: 800, fontSize: '0.98rem' }}>
                  {isMr ? (calendarCust.name_mr || calendarCust.name) : (calendarCust.name_en || calendarCust.name_mr || calendarCust.name)}
                </span>
                <span style={{ fontSize: '0.75rem', color: '#38bdf8', marginLeft: 8 }}>
                  {isMr ? '- मासिक दूध वाटप व खाडे नोंद' : '- Monthly Milk Delivery & Skipped Days'}
                </span>
              </div>
              <button onClick={() => setCalendarCust(null)} style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>

            <div style={{ padding: '1.25rem' }}>
              
              {/* Month Selector Bar */}
              <div style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                padding: '0.65rem 0.85rem',
                marginBottom: '0.85rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '0.5rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: 800, color: '#334155' }}>
                    📅 {isMr ? 'महिना निवडा:' : 'Select Month:'}
                  </span>
                  <input
                    type="month"
                    value={attendanceMonth}
                    onChange={(e) => openCalendarEditor(calendarCust, e.target.value)}
                    style={{
                      padding: '4px 8px',
                      borderRadius: 6,
                      border: '1px solid #cbd5e1',
                      fontSize: '0.82rem',
                      fontWeight: 700,
                      color: '#0284c7',
                      background: '#ffffff'
                    }}
                  />
                </div>

                <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#0284c7' }}>
                  {attendanceMonth === '2026-09' ? (isMr ? 'सप्टेंबर २०२६' : 'September 2026') : attendanceMonth}
                </div>
              </div>

              <div style={{ fontSize: '0.73rem', color: '#64748b', marginBottom: '0.65rem' }}>
                {isMr 
                  ? 'प्रत्येक दिवसाचे लिटर संपादन करा किंवा सुट्टी/नाही मार्क करा (बदल सेव्ह करण्यासाठी खालील जतन करा बटनावर क्लिक करा):' 
                  : 'Edit litres for any day or toggle absent (click Save below to persist changes):'}
              </div>

              {/* 30/31 Day Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '0.4rem', maxHeight: '340px', overflowY: 'auto', padding: '4px' }}>
                {matrixData.map((d, idx) => (
                  <div
                    key={d.day}
                    style={{
                      border: d.isAbsent ? '1.5px dashed #fca5a5' : '1px solid #cbd5e1',
                      background: d.isAbsent ? '#fef2f2' : '#f8fafc',
                      borderRadius: 8,
                      padding: '0.4rem 0.25rem',
                      textAlign: 'center',
                      fontSize: '0.72rem'
                    }}
                  >
                    <div style={{ fontWeight: 800, color: d.isAbsent ? '#dc2626' : '#475569' }}>
                      {d.day}
                    </div>
                    {d.isAbsent ? (
                      <span style={{ color: '#dc2626', fontWeight: 800, fontSize: '0.68rem', display: 'block', margin: '2px 0' }}>
                        {isMr ? 'खाडा' : 'Skipped'}
                      </span>
                    ) : (
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        value={d.litres}
                        onChange={(e) => updateMatrixDay(idx, parseFloat(e.target.value) || 0, false)}
                        style={{ width: '42px', padding: '2px', textAlign: 'center', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: '0.75rem', fontWeight: 700, margin: '2px auto', display: 'block' }}
                      />
                    )}
                    <div style={{ marginTop: 2 }}>
                      <button
                        type="button"
                        onClick={() => updateMatrixDay(idx, d.litres, !d.isAbsent)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '0.62rem',
                          fontWeight: 700,
                          color: d.isAbsent ? '#0284c7' : '#e11d48',
                          textDecoration: 'underline'
                        }}
                      >
                        {d.isAbsent ? (isMr ? 'दूध सुरू' : 'Resume') : (isMr ? 'खाडा' : 'Skip')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Bottom Actions */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', borderTop: '1px solid #f1f5f9', paddingTop: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => setCalendarCust(null)}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: 8,
                    background: '#f1f5f9',
                    color: '#475569',
                    fontWeight: 700,
                    border: '1px solid #cbd5e1',
                    fontSize: '0.8rem',
                    cursor: 'pointer'
                  }}
                >
                  {isMr ? 'रद्द करा' : 'Cancel'}
                </button>

                <button
                  type="button"
                  disabled={savingAttendance}
                  onClick={handleSaveAttendance}
                  style={{
                    padding: '0.5rem 1.4rem',
                    borderRadius: 8,
                    background: savingAttendance ? '#94a3b8' : '#0284c7',
                    color: '#ffffff',
                    fontWeight: 800,
                    border: 'none',
                    fontSize: '0.82rem',
                    cursor: savingAttendance ? 'not-allowed' : 'pointer',
                    boxShadow: '0 2px 6px rgba(2, 132, 199, 0.3)'
                  }}
                >
                  {savingAttendance 
                    ? (isMr ? 'सेव्ह होत आहे...' : 'Saving...') 
                    : (isMr ? 'बदल जतन करा (Save & Close)' : 'Save & Close')}
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Customer Modal */}
      {isModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '1rem' }}>
          <div style={{ background: '#ffffff', borderRadius: 16, maxWidth: 520, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
            <div style={{ background: '#0284c7', color: '#fff', padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 800, fontSize: '1.05rem' }}>
                {editingCust ? (isMr ? 'ग्राहक माहिती संपादन' : 'Edit Customer') : (isMr ? 'नवीन ग्राहक नोंदणी' : 'Add New Customer')}
              </span>
              <button onClick={() => setIsModalOpen(false)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}><X size={18} /></button>
            </div>

            <form onSubmit={handleSave} style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#334155' }}>{isMr ? 'ग्राहकाचे नाव (मराठीत) *' : 'Customer Name *'}</label>
                <input required type="text" placeholder="उदा. रमेश जोशी" value={formData.name_mr} onChange={e => setFormData({ ...formData, name_mr: e.target.value })} style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', marginTop: 2, boxSizing: 'border-box' }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#334155' }}>{isMr ? 'मोबाईल नंबर *' : 'Phone Number *'}</label>
                  <input required type="tel" placeholder="9822xxxxxx" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', marginTop: 2, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#334155' }}>{isMr ? 'वितरण मार्ग (Line) *' : 'Delivery Line *'}</label>
                  <select value={formData.line_id} onChange={e => setFormData({ ...formData, line_id: e.target.value })} style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', marginTop: 2, background: '#fff', boxSizing: 'border-box' }}>
                    {lines.map(l => (
                      <option key={l.id} value={l.id.toString()}>{l.name_mr || l.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#334155' }}>{isMr ? 'पूर्ण पत्ता (फ्लॅट क्र., इमारत, रस्ता) *' : 'Full Address *'}</label>
                <input required type="text" placeholder="उदा. १०१, सह्याद्री पार्क, कोथरुड" value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', marginTop: 2, boxSizing: 'border-box' }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.65rem' }}>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#334155' }}>{isMr ? 'सबस्क्रिप्शन प्रकार' : 'Subscription Type'}</label>
                  <select value={formData.subscription_type} onChange={e => setFormData({ ...formData, subscription_type: e.target.value })} style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', marginTop: 2, background: '#fff', boxSizing: 'border-box' }}>
                    <option value="DAILY">{isMr ? 'दररोज (Daily)' : 'Daily'}</option>
                    <option value="ALTERNATE">{isMr ? 'एक दिवसाआड (Alternate)' : 'Alternate'}</option>
                    <option value="ON_DEMAND">{isMr ? 'मागणीनुसार (On Demand)' : 'On Demand'}</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#334155' }}>{isMr ? 'ग्लास बाटल्या' : 'Bottles Held'}</label>
                  <input type="number" value={formData.bottles_held} onChange={e => setFormData({ ...formData, bottles_held: e.target.value })} style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', marginTop: 2, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#b45309' }}>{isMr ? 'आरंभी शिल्लक / बाकी (₹)' : 'Opening Balance (₹)'}</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    placeholder="0.00"
                    value={formData.balance} 
                    onChange={e => setFormData({ ...formData, balance: e.target.value })} 
                    style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #f59e0b', marginTop: 2, background: '#fffbeb', color: '#92400e', fontWeight: 700, boxSizing: 'border-box' }} 
                  />
                </div>
              </div>

              <div style={{ background: '#f8fafc', padding: '0.75rem', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#0f172a', marginBottom: '0.4rem' }}>
                  {isMr ? 'दैनिक दूध कोटा वाटप (लिटरमध्ये):' : 'Daily Milk Quota Allocation (Litres):'}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
                  <div>
                    <label style={{ fontSize: '0.72rem', color: '#d97706', fontWeight: 700 }}>{isMr ? 'सकाळ गाय दूध' : 'Morning Cow'}</label>
                    <input type="number" step="0.5" value={formData.morning_cow_qty} onChange={e => setFormData({ ...formData, morning_cow_qty: e.target.value })} style={{ width: '100%', padding: '0.4rem', borderRadius: 6, border: '1px solid #cbd5e1', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.72rem', color: '#4f46e5', fontWeight: 700 }}>{isMr ? 'सकाळ म्हैस दूध' : 'Morning Buffalo'}</label>
                    <input type="number" step="0.5" value={formData.morning_buf_qty} onChange={e => setFormData({ ...formData, morning_buf_qty: e.target.value })} style={{ width: '100%', padding: '0.4rem', borderRadius: 6, border: '1px solid #cbd5e1', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.72rem', color: '#d97706', fontWeight: 700 }}>{isMr ? 'संध्याकाळ गाय दूध' : 'Evening Cow'}</label>
                    <input type="number" step="0.5" value={formData.evening_cow_qty} onChange={e => setFormData({ ...formData, evening_cow_qty: e.target.value })} style={{ width: '100%', padding: '0.4rem', borderRadius: 6, border: '1px solid #cbd5e1', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.72rem', color: '#4f46e5', fontWeight: 700 }}>{isMr ? 'संध्याकाळ म्हैस दूध' : 'Evening Buffalo'}</label>
                    <input type="number" step="0.5" value={formData.evening_buf_qty} onChange={e => setFormData({ ...formData, evening_buf_qty: e.target.value })} style={{ width: '100%', padding: '0.4rem', borderRadius: 6, border: '1px solid #cbd5e1', boxSizing: 'border-box' }} />
                  </div>
                </div>
              </div>

              
              <div style={{ marginTop: '0.8rem', background: '#f8fafc', padding: '0.75rem', borderRadius: 8, border: '1px solid #cbd5e1' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 800, fontSize: '0.82rem', color: '#1e293b' }}>
                  <input
                    type="checkbox"
                    checked={Boolean(formData.is_dynamic_rate)}
                    onChange={e => setFormData({ ...formData, is_dynamic_rate: e.target.checked })}
                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                  />
                  <span>{isMr ? 'दररोज दर बदलणारा ग्राहक (Dynamic Daily Rate)' : 'Dynamic Daily Rate Customer'}</span>
                </label>
                <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 4, marginLeft: 26 }}>
                  {isMr ? 'हा पर्याय चालू ठेवल्यास वाटप करताना या ग्राहकाचा दूध विक्री दर दररोज बदलता येईल.' : 'If enabled, milk selling rate can be dynamically adjusted for this customer during delivery.'}
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
                {editingCust ? (
                  <button
                    type="button"
                    onClick={() => {
                      const toDel = editingCust;
                      setIsModalOpen(false);
                      setDeleteConfirmCust(toDel);
                    }}
                    style={{
                      padding: '0.55rem 0.9rem',
                      borderRadius: 8,
                      border: '1px solid #fecaca',
                      background: '#fef2f2',
                      color: '#dc2626',
                      fontWeight: 700,
                      fontSize: '0.82rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}
                  >
                    <Trash2 size={14} />
                    <span>{isMr ? 'खाते डिलीट करा' : 'Delete Account'}</span>
                  </button>
                ) : <div />}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="button" onClick={() => setIsModalOpen(false)} style={{ padding: '0.55rem 1rem', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f1f5f9', fontWeight: 700, cursor: 'pointer' }}>{isMr ? 'रद्द करा' : 'Cancel'}</button>
                  <button type="submit" disabled={submitting} style={{ padding: '0.55rem 1.3rem', borderRadius: 8, border: 'none', background: submitting ? '#94a3b8' : '#0284c7', color: '#fff', fontWeight: 800, cursor: submitting ? 'not-allowed' : 'pointer' }}>{submitting ? (isMr ? 'सेव्ह होत आहे...' : 'Saving...') : (isMr ? 'ग्राहक जतन करा' : 'Save Customer')}</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmCust && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 120, padding: '1rem' }}>
          <div style={{ background: '#ffffff', borderRadius: 16, maxWidth: 460, width: '100%', overflow: 'hidden', padding: '1.5rem', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)', border: '1px solid #fecaca' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: '#dc2626', marginBottom: '1rem' }}>
              <div style={{ background: '#fee2e2', padding: 10, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AlertCircle size={28} color="#dc2626" />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 900, color: '#0f172a' }}>
                  {isMr ? 'ग्राहक खाते डिलीट करायचे का?' : 'Delete Customer Account?'}
                </h3>
                <div style={{ fontSize: '0.85rem', color: '#dc2626', fontWeight: 700, marginTop: 2 }}>
                  {deleteConfirmCust.name_mr || deleteConfirmCust.name}
                </div>
              </div>
            </div>

            <div style={{ background: '#fff1f2', border: '1.5px solid #fecdd3', borderRadius: 12, padding: '0.85rem 1rem', marginBottom: '1.25rem' }}>
              <p style={{ fontSize: '0.88rem', color: '#991b1b', lineHeight: 1.6, margin: 0, fontWeight: 700 }}>
                {isMr
                  ? 'या ग्राहकाचे खाते डिलीट केल्यास त्याचे सगळे रेकॉर्डस डिलीट होतील आणि पुन्हा मिळू शकणार नाहीत तरी डिलीट करण्यापूर्वी खात्री करा.'
                  : 'Deleting this customer account will permanently remove all associated records (deliveries, payment receipts, attendance, and billing ledger). This action cannot be undone. Please confirm before proceeding.'}
              </p>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                type="button"
                onClick={() => setDeleteConfirmCust(null)}
                style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', padding: '0.6rem 1.1rem', borderRadius: 8, fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
              >
                {isMr ? 'रद्द करा' : 'Cancel'}
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={handleDeleteCustomer}
                style={{ background: '#dc2626', color: '#ffffff', border: 'none', padding: '0.6rem 1.25rem', borderRadius: 8, fontWeight: 800, fontSize: '0.85rem', cursor: submitting ? 'not-allowed' : 'pointer', boxShadow: '0 4px 6px -1px rgba(220, 38, 38, 0.3)' }}
              >
                {submitting ? (isMr ? 'डिलीट होत आहे...' : 'Deleting...') : (isMr ? 'होय, खाते कायमचे डिलीट करा' : 'Yes, Permanently Delete')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
